import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for session expiry
  const checkSessionExpiry = useCallback(() => {
    const loginTimestamp = localStorage.getItem('loginTimestamp');

    if (loginTimestamp && isAuthenticated) {
      const currentTime = Date.now();
      const timeDiff = currentTime - parseInt(loginTimestamp);
      const hoursPassed = timeDiff / (1000 * 60 * 60);

      if (hoursPassed >= 24) {
        // Auto-logout after 24 hours
        console.log('Session expired: 24 hours have passed');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('loginTimestamp');
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login';
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const loginTimestamp = localStorage.getItem('loginTimestamp');

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);

        // Check if 24 hours have passed since login
        if (loginTimestamp) {
          const currentTime = Date.now();
          const timeDiff = currentTime - parseInt(loginTimestamp);
          const hoursPassed = timeDiff / (1000 * 60 * 60);

          if (hoursPassed >= 24) {
            // Auto-logout after 24 hours
            console.log('Session expired: 24 hours have passed');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('loginTimestamp');
            setLoading(false);
            return;
          }
        }

        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Failed to parse user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('loginTimestamp');
      }
    }

    setLoading(false);
  }, []);

  // Periodic check for session expiry (every minute)
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      checkSessionExpiry();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, checkSessionExpiry]);

  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('loginTimestamp', Date.now().toString());
    setUser(userData);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTimestamp');
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const isAdmin = useCallback(() => {
    return user?.role === 'admin';
  }, [user?.role]);

  const isUser = useCallback(() => {
    return user?.role === 'user';
  }, [user?.role]);

  const hasPermission = useCallback((permission) => {
    // Admin has all permissions
    if (user?.role === 'admin') {
      return true;
    }

    // Define user permissions (read-only)
    const userPermissions = ['view', 'read'];

    return userPermissions.includes(permission);
  }, [user?.role]);

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    isUser,
    hasPermission,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
